/**
 * Site-wide smooth scroll for same-page anchor links.
 * Uses `.page-wrapper` as the scroll container on desktop (Horizon layout).
 */
(() => {
  const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
  const HEADER_SELECTOR = '#header-group, header-component, .header-section';
  const DESKTOP_MQ = '(min-width: 990px)';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getScrollContainer = () => {
    const wrapper = document.querySelector(PAGE_WRAPPER_SELECTOR);
    if (wrapper && window.matchMedia(DESKTOP_MQ).matches) {
      return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  };

  const getHeaderOffset = () => {
    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) return 0;
    return Math.ceil(header.getBoundingClientRect().height || 0);
  };

  const resolveTarget = (hash) => {
    if (!hash || hash === '#') return null;
    try {
      return document.querySelector(decodeURIComponent(hash));
    } catch {
      return document.getElementById(hash.slice(1));
    }
  };

  const smoothScrollToElement = (element, { updateHash = true } = {}) => {
    if (!element) return;

    const container = getScrollContainer();
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const offset = getHeaderOffset() + 12;

    if (container === document.scrollingElement || container === document.documentElement || container === document.body) {
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior });
    } else {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const top = elementRect.top - containerRect.top + container.scrollTop - offset;
      container.scrollTo({ top: Math.max(0, top), behavior });
    }

    if (updateHash && element.id) {
      const url = `${window.location.pathname}${window.location.search}#${element.id}`;
      history.pushState(null, '', url);
    }
  };

  const onClick = (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;
    if (!url.hash) return;

    const target = resolveTarget(url.hash);
    if (!target) return;

    event.preventDefault();
    smoothScrollToElement(target);
  };

  const scrollFromHash = () => {
    if (!window.location.hash) return;
    const target = resolveTarget(window.location.hash);
    if (!target) return;
    // Defer so layout/header height are ready
    requestAnimationFrame(() => {
      smoothScrollToElement(target, { updateHash: false });
    });
  };

  document.addEventListener('click', onClick);
  window.addEventListener('load', scrollFromHash);
  window.addEventListener('hashchange', scrollFromHash);

  if (document.readyState === 'complete') {
    scrollFromHash();
  }
})();
